import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers';
import {
  addDays,
  differenceInCalendarDays,
  format as formatDate,
  parse as parseDate,
  parseISO,
} from 'date-fns';
import React from 'react';
import type {
  AppDateChangeHandler,
  AppDateValue,
} from 'shared/datePickerTypes';
import { ReportDateFieldType } from 'shared/propTypes';
import slugify from 'slugify';

const DISPLAY_FORMAT = 'MM/dd/yyyy';
const DISPLAY_PARSE_FORMAT = 'MM/dd/yyyy';
const TOOLBAR_FORMAT = 'MMM dd, yyyy';

type DatePickerValue = AppDateValue | string | undefined;

interface DatePickerProps {
  id?: string;
  label?: string;
  name?: string;
  value?: DatePickerValue;
  onChange: AppDateChangeHandler;
  formField?: ReportDateFieldType;
  disablePast?: boolean;
  customDisableBeforeDate?: Date;
  disableField?: boolean;
  disabled?: boolean;
  selectYearOnly?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  className?: string;
  displayFormat?: string;
  toolbarFormat?: string;
}

const isValidDate = (value: unknown): value is Date => (
  value instanceof Date && !Number.isNaN(value.getTime())
);

const normalizeDateValue = (
  value: DatePickerValue,
  selectYearOnly: boolean,
): AppDateValue => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (isValidDate(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return null;
    }

    const candidateFormats = selectYearOnly
      ? ['yyyy']
      : ['yyyy-MM-dd', DISPLAY_PARSE_FORMAT];

    for (const fmt of candidateFormats) {
      const parsed = parseDate(trimmedValue, fmt, new Date());

      if (isValidDate(parsed)) {
        return parsed;
      }
    }

    const isoParsed = parseISO(trimmedValue);

    if (isValidDate(isoParsed)) {
      return isoParsed;
    }

    const fallback = new Date(trimmedValue);

    return isValidDate(fallback) ? fallback : null;
  }

  return null;
};

const formatOutputValue = (
  value: AppDateValue,
  selectYearOnly: boolean,
): string | null => (
  value ? formatDate(value, selectYearOnly ? 'yyyy' : 'yyyy-MM-dd') : null
);

const DatePicker: React.FC<DatePickerProps> = ({
  className,
  customDisableBeforeDate,
  disableField = false,
  disablePast = false,
  disabled = false,
  error = false,
  fullWidth = false,
  helperText,
  id,
  label,
  name,
  onChange,
  required = false,
  selectYearOnly = false,
  displayFormat,
  toolbarFormat,
  value,
}) => {
  const resolvedId = id || slugify(label || 'date-picker');
  const resolvedValue = normalizeDateValue(value, selectYearOnly);

  const handleChange = (nextValue: AppDateValue) => {
    if (!isValidDate(nextValue)) {
      onChange(null);

      return;
    }

    onChange(nextValue, formatOutputValue(nextValue, selectYearOnly));
  };

  const shouldDisableDate = (candidateDate: Date) => {
    if (disablePast) {
      return differenceInCalendarDays(candidateDate, new Date()) < 0;
    }

    if (customDisableBeforeDate) {
      return differenceInCalendarDays(
        candidateDate,
        addDays(customDisableBeforeDate, 1),
      ) < 0;
    }

    return false;
  };

  return (
    <MuiDatePicker
      closeOnSelect
      disabled={disabled || disableField}
      disablePast={disablePast}
      format={displayFormat || (selectYearOnly ? 'yyyy' : DISPLAY_FORMAT)}
      label={label}
      onChange={handleChange}
      openTo={selectYearOnly ? 'year' : 'day'}
      shouldDisableDate={shouldDisableDate}
      slotProps={{
        textField: {
          className,
          error,
          fullWidth,
          helperText,
          id: resolvedId,
          name,
          required,
          variant: 'standard',
        },
        toolbar: {
          hidden: false,
          toolbarFormat: toolbarFormat || (selectYearOnly ? 'yyyy' : TOOLBAR_FORMAT),
          toolbarPlaceholder: selectYearOnly ? 'YYYY' : 'MMM DD, YYYY',
        },
      }}
      value={resolvedValue}
      views={selectYearOnly ? ['year'] : undefined}
      yearsPerRow={4}
    />
  );
};

export default DatePicker;
